// frontend/src/components/LandingPage/IntegrationsSection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
    Shield, 
    Lock, 
    Database,
    Cloud,
    Zap,
    CheckCircle2
} from 'lucide-react';

const technologies = [
    { name: 'React', description: 'Interface moderna e responsiva' },
    { name: 'PostgreSQL', description: 'Banco de dados robusto e confiável' },
    { name: 'Node.js', description: 'Backend escalável e performático' },
    { name: 'Tailwind CSS', description: 'Design system consistente' }
];

const securityFeatures = [
    { icon: Shield, text: 'LGPD Compliant' },
    { icon: Lock, text: 'Criptografia SSL' },
    { icon: Database, text: 'Backup Automático' },
    { icon: Cloud, text: 'Infraestrutura Cloud' }
];

const IntegrationsSection = () => {
    return (
        <section className="py-20 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Cabeçalho */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                        Construído com as{' '}
                        <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                            melhores tecnologias
                        </span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Uma arquitetura moderna, segura e escalável para garantir o melhor desempenho e confiabilidade.
                    </p>
                </motion.div>

                {/* Grid de Tecnologias */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
                    {technologies.map((tech, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-2 text-center"
                        >
                            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-higeia-light to-higeia-dark rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Zap className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2">{tech.name}</h3>
                            <p className="text-sm text-gray-600">{tech.description}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Seção de Segurança */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-3xl p-8 sm:p-12 text-white shadow-2xl"
                >
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                                <Shield className="h-8 w-8" />
                            </div>
                            <h3 className="text-3xl font-bold mb-4">
                                Segurança e Confiabilidade em Primeiro Lugar
                            </h3>
                            <p className="text-lg text-white/90">
                                Todos os seus dados são protegidos com as melhores práticas de segurança e conformidade.
                            </p>
                        </div>

                        {/* Grid de Features de Segurança */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {securityFeatures.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex flex-col items-center text-center"
                                    >
                                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3 backdrop-blur-sm">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <span className="text-sm font-medium">{feature.text}</span>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Badges de Conformidade */}
                        <div className="mt-8 flex flex-wrap justify-center gap-4">
                            {['LGPD', 'ISO 27001', 'HIPAA Ready'].map((badge, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4 + index * 0.1 }}
                                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full"
                                >
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span className="text-sm font-medium">{badge}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default IntegrationsSection;

